import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, Loader2, Play, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabaseClient";
import { SortableTableHeader } from "@/components/admin/SortableTableHeader";
import { nextTableSort, sortTableRows, type TableSort } from "@/lib/adminTableSort";
import { SOUND_COLUMNS, soundSortValue, type SoundSortKey } from "@/lib/soundEffectsSort";

type Library = { id: string; name: string; creator: string; source_url: string; license_url: string };
type Sound = { id: string; library_id: string; name: string; category: string; duration_seconds: number; storage_path: string };
const PAGE_SIZE = 200;

export default function SoundEffectsTab() {
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [sounds, setSounds] = useState<Sound[]>([]);
  const [library, setLibrary] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<TableSort<SoundSortKey>>({ key: "name", direction: "ascending" });
  const [preview, setPreview] = useState<{ sound: Sound; url: string } | null>(null);
  const [preparing, setPreparing] = useState<string | null>(null);
  const previewRequest = useRef(0);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(null);
    void (async () => {
      try {
        const result = await supabase.from("sound_effect_libraries").select("id,name,creator,source_url,license_url").order("name");
        if (result.error) throw result.error;
        const collected: Sound[] = [];
        // Stable order and explicit pagination avoid Supabase's default row limit.
        for (let start = 0; ; start += PAGE_SIZE) {
          const response = await supabase.from("sound_effects")
            .select("id,library_id,name,category,duration_seconds,storage_path")
            .order("name").order("id").range(start, start + PAGE_SIZE - 1);
          if (!active) return;
          if (response.error) throw response.error;
          collected.push(...(response.data ?? []) as Sound[]);
          if ((response.data?.length ?? 0) < PAGE_SIZE) break;
        }
        if (active) { setLibraries((result.data ?? []) as Library[]); setSounds(collected); }
      } catch { if (active) setError("The sound library could not be loaded. Check administrator access and try Refresh."); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [revision]);
  useEffect(() => () => { previewRequest.current++; }, []);

  const byId = useMemo(() => new Map(libraries.map(item => [item.id, item])), [libraries]);
  const filtered = useMemo(() => sounds.filter(sound => (!library || sound.library_id === library) &&
    `${sound.name} ${sound.category} ${byId.get(sound.library_id)?.name ?? ""}`.toLowerCase().includes(search.trim().toLowerCase())),
  [sounds, library, search, byId]);
  const sorted = useMemo(() => sortTableRows(filtered,
    sound => soundSortValue(sound, sort.key, byId), sort.direction), [filtered, sort, byId]);
  function changeSort(key: SoundSortKey) {
    setSort(current => nextTableSort(current, key));
    setPage(0);
  }
  const pages = Math.max(1, Math.ceil(filtered.length / 50));
  const currentPage = Math.min(page, pages - 1);

  async function audition(sound: Sound) {
    const request = ++previewRequest.current;
    setPreparing(sound.id); setPreview(null); setError(null);
    try {
      const { data, error: signingError } = await supabase.storage.from("sound-effects").createSignedUrl(sound.storage_path, 3600);
      if (signingError || !data?.signedUrl) throw signingError;
      if (request === previewRequest.current) setPreview({ sound, url: data.signedUrl });
    } catch { if (request === previewRequest.current) setError("This sound could not be opened. Try again or refresh your sign-in."); }
    finally { if (request === previewRequest.current) setPreparing(null); }
  }

  return <section className="space-y-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><h2 className="text-xl font-semibold">Sound effects</h2>
        <p className="mt-1 text-sm text-muted-foreground">CC0 libraries for TableRead. Audition individual sounds and review their sources.</p></div>
      <Button variant="outline" disabled={loading} onClick={() => setRevision(value => value + 1)}>
        <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />Refresh
      </Button>
    </div>
    <div className="grid gap-3 lg:grid-cols-2">
      {libraries.map(item => <article key={item.id} className="rounded-lg border bg-muted/20 p-3">
        <div className="flex items-start justify-between gap-3"><h3 className="font-medium">{item.name}</h3>
          <span className="shrink-0 text-xs text-muted-foreground">{sounds.filter(sound => sound.library_id === item.id).length} sounds</span></div>
        <p className="mt-1 text-xs text-muted-foreground">{item.creator}</p>
        <div className="mt-2 flex gap-4 text-xs">
          <a className="inline-flex items-center gap-1 underline" href={item.source_url} target="_blank" rel="noreferrer">Source <ExternalLink className="h-3 w-3" /></a>
          <a className="underline" href={item.license_url} target="_blank" rel="noreferrer">CC0 1.0 · public domain dedication</a>
        </div>
      </article>)}
    </div>
    <div className="flex flex-wrap gap-3">
      <Input className="max-w-sm" type="search" aria-label="Search sound effects" placeholder="Search sound effects…"
        value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} />
      <select aria-label="Sound effect library" className="max-w-full rounded-md border bg-background p-2 text-sm"
        value={library} onChange={event => { setLibrary(event.target.value); setPage(0); }}>
        <option value="">All libraries</option>{libraries.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
    </div>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {preview && <div className="rounded-lg border bg-muted/20 p-3" aria-label="Sound preview">
      <p className="mb-2 text-sm font-medium">{preview.sound.name}</p>
      <audio key={preview.url} controls autoPlay preload="metadata" className="w-full max-w-xl" src={preview.url}
        aria-label={`Preview ${preview.sound.name}`} onError={() => setError("Audio playback failed. Select Preview again to renew the link.")} />
    </div>}
    <p role="status" className="text-sm text-muted-foreground">{loading ? "Loading sound effects…" : `${filtered.length.toLocaleString()} matching · ${sounds.length.toLocaleString()} total sound effects`}</p>
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[600px] text-sm">
        <thead><tr className="border-b text-left text-muted-foreground">
          {SOUND_COLUMNS.map(({ key, label }) => <SortableTableHeader key={key} column={key} label={label}
            sort={sort} onSort={changeSort} className="px-3 py-3" />)}
          <th scope="col" className="px-3 py-3">Preview</th>
        </tr></thead>
        <tbody>{sorted.slice(currentPage * 50, (currentPage + 1) * 50).map(sound => <tr key={sound.id} className="border-b last:border-0">
          <td className="px-3 py-3 font-medium">{sound.name}</td><td className="px-3 py-3">{byId.get(sound.library_id)?.name}</td>
          <td className="px-3 py-3 text-muted-foreground">{sound.category}</td><td className="px-3 py-3 tabular-nums">{sound.duration_seconds.toFixed(1)} s</td>
          <td className="px-3 py-2"><Button size="icon" variant="outline" aria-label={`Preview ${sound.name}`} title={`Preview ${sound.name}`}
            disabled={preparing === sound.id} onClick={() => void audition(sound)}>
            {preparing === sound.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          </Button></td>
        </tr>)}{!loading && !filtered.length && <tr><td colSpan={5} className="p-6 text-muted-foreground">No matching sound effects.</td></tr>}</tbody>
      </table>
    </div>
    <div className="flex items-center justify-end gap-3 text-sm">
      <Button variant="outline" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</Button>
      <span>Page {currentPage + 1} of {pages}</span>
      <Button variant="outline" disabled={currentPage + 1 >= pages} onClick={() => setPage(currentPage + 1)}>Next</Button>
    </div>
  </section>;
}
